<template>
  <section class="dp-layout sc-cal-layout">
    <section class="dp-left">
      <DepartmentPane directory-scope="school" @tree-node-click="treeNodeClick" :type="1"></DepartmentPane>
    </section>

    <section class="dp-right sc-cal-page">
      <div class="sc-cal-shell">
        <header class="sc-cal-toolbar">
          <div class="sc-cal-seg" role="tablist">
            <button
              type="button"
              class="sc-cal-seg__btn"
              :class="{ 'is-active': viewMode === 'class' }"
              @click="switchViewMode('class')"
            >
              <i class="el-icon-office-building"></i>
              <span>按班级</span>
            </button>
            <button
              type="button"
              class="sc-cal-seg__btn"
              :class="{ 'is-active': viewMode === 'teacher' }"
              @click="switchViewMode('teacher')"
            >
              <i class="el-icon-user"></i>
              <span>按教师</span>
            </button>
          </div>

          <div class="sc-cal-filters">
            <div class="sc-cal-field">
              <span class="sc-cal-field__label">校区</span>
              <el-select
                v-model="campusId"
                placeholder="选择校区"
                clearable
                filterable
                size="small"
                class="sc-cal-field__ctrl"
                @change="onCampusFilterChange"
              >
                <el-option v-for="c in campusOptions" :key="c.Id" :label="c.Name" :value="c.Id" />
              </el-select>
            </div>
            <div v-if="viewMode === 'class'" class="sc-cal-field">
              <span class="sc-cal-field__label">行政班</span>
              <el-select
                v-model="classDepartmentId"
                placeholder="选择行政班"
                clearable
                filterable
                size="small"
                class="sc-cal-field__ctrl"
              >
                <el-option v-for="c in classOptions" :key="c.Id" :label="c.Name" :value="c.Id" />
              </el-select>
            </div>
            <div v-else class="sc-cal-field">
              <span class="sc-cal-field__label">主教</span>
              <el-select
                v-model="teacherMasterId"
                placeholder="选择主教"
                clearable
                filterable
                size="small"
                class="sc-cal-field__ctrl"
                :loading="teacherLoading"
              >
                <el-option
                  v-for="t in teacherOptions"
                  :key="t.value"
                  :label="t.label"
                  :value="t.value"
                />
              </el-select>
            </div>
            <el-button type="primary" size="small" icon="el-icon-search" :loading="loading" class="sc-cal-query"
              @click="loadCalendar">查询</el-button>
          </div>

          <el-button size="small" plain icon="el-icon-document" class="sc-cal-changelog-btn"
            @click="openChangeLogDialog">代调变动记录</el-button>
        </header>

        <div class="sc-cal-monthbar">
          <div class="sc-cal-monthbar__title">
            <span class="sc-cal-monthbar__ym">{{ calendarMonthLabel }}</span>
            <span class="sc-cal-monthbar__hint">{{ viewModeHint }}</span>
          </div>
          <div class="sc-cal-monthbar__nav">
            <el-button size="mini" icon="el-icon-arrow-left" @click="shiftMonth(-1)">上月</el-button>
            <el-button size="mini" @click="goToday">今天</el-button>
            <el-button size="mini" @click="shiftMonth(1)">下月<i class="el-icon-arrow-right el-icon--right"></i></el-button>
          </div>
        </div>

        <div class="sc-cal-body" v-loading="loading">
          <el-calendar v-model="calendarMonth" class="sc-cal-widget">
            <template slot="dateCell" slot-scope="{ data }">
              <div
                class="sc-cal-cell"
                :class="{
                  'is-other': data.type !== 'current-month',
                  'is-today': isToday(data.day),
                  'has-events': eventsForDay(data.day).length > 0
                }"
              >
                <div class="sc-cal-cell__top">
                  <span class="sc-cal-cell__day">{{ dayNum(data.day) }}</span>
                  <span v-if="eventsForDay(data.day).length" class="sc-cal-cell__count">
                    {{ eventsForDay(data.day).length }}
                  </span>
                </div>
                <div class="sc-cal-cell__events">
                  <div
                    v-for="(ev, idx) in eventsForDay(data.day)"
                    :key="data.day + '-' + (ev.sessionId || ev.SessionId || idx) + '-' + idx"
                    class="sc-cal-ev"
                    :class="eventToneClass(ev)"
                  >
                    <div class="sc-cal-ev__time" v-if="field(ev, 'timeName')">{{ field(ev, 'timeName') }}</div>
                    <div class="sc-cal-ev__head" :title="eventTitleLine(ev)">
                      {{ field(ev, 'sessionName') || '—' }}
                    </div>
                    <div
                      v-if="viewMode === 'class' && teachersJoined(ev)"
                      class="sc-cal-ev__meta"
                      :title="teachersJoined(ev)"
                    >
                      <i class="el-icon-user"></i>{{ teachersJoined(ev) }}
                    </div>
                    <div
                      v-else-if="viewMode === 'teacher'"
                      class="sc-cal-ev__meta"
                      :title="field(ev, 'className') || ''"
                    >
                      <i class="el-icon-office-building"></i>{{ field(ev, 'className') || '—' }}
                    </div>
                    <div
                      v-if="field(ev, 'hasAdjustmentRecord') || field(ev, 'HasAdjustmentRecord')"
                      class="sc-cal-ev__adjblock"
                    >
                      <el-tooltip
                        v-if="adjustmentTooltipFull(ev)"
                        effect="dark"
                        placement="top"
                        :content="adjustmentTooltipFull(ev)"
                      >
                        <div class="sc-cal-ev__adjline">
                          <span class="sc-cal-ev__adj-pill">代调</span>
                          <span class="sc-cal-ev__adj-phrase">{{ adjustmentPrimaryLine(ev) }}</span>
                        </div>
                      </el-tooltip>
                      <div v-else class="sc-cal-ev__adjline">
                        <span class="sc-cal-ev__adj-pill">代调</span>
                        <span class="sc-cal-ev__adj-phrase">—</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </el-calendar>
        </div>
      </div>

      <el-dialog 
        title="代调课变动记录"
        :visible.sync="changeLogDialogVisible"
        :close-on-click-modal="false"
        append-to-body
        width="800px"
      >
        <div v-loading="changeLogDialogLoading" class="sc-cal-changelog__body">
          <el-table
            v-if="!changeLogDialogLoading && changeLogRows.length"
            :data="changeLogPagedRows"
            class="custom-tbl"
            style="width: 100%"
          >
            <el-table-column label="时间" width="100">
              <template slot-scope="scope">
                <div class="sc-cal-chg-stack">
                  <div>{{ scope.row.Date || scope.row.date }}</div>
                  <div class="sc-cal-chg-sub">{{ scope.row.TimeName || scope.row.timeName || '—' }}</div>
                </div>
              </template>
            </el-table-column>
            <el-table-column label="班次 / 班级" min-width="150">
              <template slot-scope="scope">
                <div class="sc-cal-chg-stack" :title="(scope.row.SessionName || scope.row.sessionName || '') + ' / ' + (scope.row.ClassName || scope.row.className || '')">
                  <div class="sc-cal-chg-ellipsis">{{ scope.row.SessionName || scope.row.sessionName || '—' }}</div>
                  <div class="sc-cal-chg-sub sc-cal-chg-ellipsis">{{ scope.row.ClassName || scope.row.className || '—' }}</div>
                </div>
              </template>
            </el-table-column>
            <el-table-column label="类型" width="64" align="center">
              <template slot-scope="scope">{{ scope.row.HandleKind || scope.row.handleKind || '—' }}</template>
            </el-table-column>
            <el-table-column label="教师" min-width="120">
              <template slot-scope="scope">
                <div class="sc-cal-chg-stack" :title="logRowSummary(scope.row)">
                  <div>原：{{ scope.row.FromTeacher || scope.row.fromTeacher || '—' }}</div>
                  <div class="sc-cal-chg-sub">现：{{ scope.row.ToTeacher || scope.row.toTeacher || '—' }}</div>
                </div>
              </template>
            </el-table-column>
            <el-table-column label="原因" min-width="200">
              <template slot-scope="scope">
                <div class="sc-cal-chg-stack sc-cal-chg-reason" :title="changeLogReasonFull(scope.row)">
                  <div class="sc-cal-chg-reason-type">{{ scope.row.ReasonTypeName || scope.row.reasonTypeName || '—' }}</div>
                  <div v-if="changeLogHasReasonDetail(scope.row)" class="sc-cal-chg-sub sc-cal-chg-ellipsis-2">
                    {{ changeLogReasonText(scope.row) }}
                  </div>
                </div>
              </template>
            </el-table-column>
          </el-table>
          <el-col
            v-if="!changeLogDialogLoading && changeLogRows.length"
            :span="24"
            class="toolbar"
          >
            <el-pagination
              layout="total, prev, pager, next, jumper"
              :current-page.sync="changeLogPage"
              :page-size="changeLogPageSize"
              :total="changeLogRows.length"
              style="float: right"
            />
          </el-col>
          <p v-else-if="!changeLogDialogLoading" class="sc-cal-changelog__empty">暂无变动记录</p>
        </div>
        <div slot="footer" class="dialog-footer">
          <el-button @click="changeLogDialogVisible = false">关 闭</el-button>
        </div>
      </el-dialog>
    </section>
  </section>
</template>

<script>
import { getSpecialCourseScheduleCalendar, getSpecialCourseScheduleChangeLog } from '../../api/modules/schedule-course';
import { getSubDepartmentList } from '../../api/modules/department';
import { getCampusList } from '../../api/modules/misc';
import { getTeacherMasterListPage } from '../../api/modules/personnel';
import DepartmentPane from '../../components/DepartmentPane'
import { normalizeDeptId, deptIdsEqual } from '../../utils/deptCascaderProps'

export default {
  name: 'SpecialCourseScheduleCalendar',
  components: { DepartmentPane },
  data() {
    return {
      departmentId: 0,
      viewMode: 'class',
      campusId: null,
      classDepartmentId: null,
      classOptions: [],
      campusOptions: [],
      teacherMasterId: null,
      teacherOptions: [],
      teacherLoading: false,
      calendarMonth: new Date(),
      calendarDays: [],
      changeLogRows: [],
      changeLogPage: 1,
      changeLogPageSize: 5,
      changeLogDialogVisible: false,
      changeLogDialogLoading: false,
      loading: false
    }
  },
  computed: {
    /** 仅年月，用于区分「换月」与「点击某日」：el-calendar 的 v-model 在点击日期时会变，但不应重复请求 */
    calendarYearMonthKey() {
      const d = this.calendarMonth
      if (!d) return ''
      return `${d.getFullYear()}-${d.getMonth()}`
    },
    calendarMonthLabel() {
      const d = this.calendarMonth
      if (!d) return ''
      return `${d.getFullYear()}年${d.getMonth() + 1}月`
    },
    viewModeHint() {
      if (this.viewMode === 'class') {
        const c = (this.classOptions || []).find(x => deptIdsEqual(x.Id, this.classDepartmentId))
        return c && c.Name ? `班级视图 · ${c.Name}` : '班级视图'
      }
      const t = (this.teacherOptions || []).find(x => String(x.value) === String(this.teacherMasterId))
      return t && t.label ? `教师视图 · ${t.label}` : '教师视图'
    },
    /**
     * 后端 Newtonsoft DefaultContractResolver 输出 PascalCase（Date、Events），需同时兼容 camelCase
     */
    eventsByDate() {
      const m = {}
      ;(this.calendarDays || []).forEach((d) => {
        if (!d) return
        const dayKey = d.date != null ? d.date : d.Date
        if (!dayKey) return
        const evs = d.events != null ? d.events : d.Events
        m[dayKey] = Array.isArray(evs) ? evs : []
      })
      return m
    },
    /** 代调变动记录弹窗：每页 5 条 */
    changeLogPagedRows() {
      const list = this.changeLogRows || []
      const start = (this.changeLogPage - 1) * this.changeLogPageSize
      return list.slice(start, start + this.changeLogPageSize)
    },
  },
  methods: {
    dayNum(dayStr) {
      if (!dayStr || typeof dayStr !== 'string') return ''
      const parts = dayStr.split('-')
      return parts.length >= 3 ? String(Number(parts[2]) || parts[2]) : dayStr
    },
    isToday(dayStr) {
      if (!dayStr) return false
      return dayStr === this.fmtDate(new Date())
    },
    /** 按时段名称给卡片着色素，便于扫读早/晚自习 */
    eventToneClass(ev) {
      const name = String(this.field(ev, 'timeName') || '')
      if (/早/.test(name)) return 'tone-morning'
      if (/晚.*1|晚自习1|第一节/.test(name)) return 'tone-eve1'
      if (/晚.*2|晚自习2|第二节/.test(name)) return 'tone-eve2'
      if (/午|中午/.test(name)) return 'tone-noon'
      const hasAdj = this.field(ev, 'hasAdjustmentRecord') || this.field(ev, 'HasAdjustmentRecord')
      if (hasAdj) return 'tone-adj'
      return 'tone-default'
    },
    switchViewMode(mode) {
      if (mode !== 'class' && mode !== 'teacher') return
      if (this.viewMode === mode) return
      this.viewMode = mode
      this.onTabChange()
    },
    shiftMonth(delta) {
      const d = this.calendarMonth ? new Date(this.calendarMonth) : new Date()
      this.calendarMonth = new Date(d.getFullYear(), d.getMonth() + delta, 1)
    },
    goToday() {
      this.calendarMonth = new Date()
    },
    eventsForDay(dayStr) {
      return this.eventsByDate[dayStr] || []
    },
    field(ev, camel) {
      const p = camel.charAt(0).toUpperCase() + camel.slice(1)
      const v = ev[camel] != null ? ev[camel] : ev[p]
      return v
    },
    teacherNames(ev) {
      const teachers = this.field(ev, 'teachers') || []
      if (!teachers.length) return []
      return teachers.map((t) => {
        const name = t.teacherName != null ? t.teacherName : t.TeacherName
        return name || ''
      }).filter(Boolean)
    },
    adjustmentChangeLines(ev) {
      const raw = this.field(ev, 'adjustmentChangeLines')
      return Array.isArray(raw) ? raw : []
    },
    /** 代课：现教师 代替 原教师；对调/教务同理用清晰中文，避免「原→现」歧义 */
    lineSummary(ln) {
      return this.formatAdjustmentSummary({
        handleType: ln.handleType != null ? ln.handleType : ln.HandleType,
        handleKind: ln.handleKind != null ? ln.handleKind : ln.HandleKind,
        fromTeacher: ln.fromTeacher != null ? ln.fromTeacher : ln.FromTeacher,
        toTeacher: ln.toTeacher != null ? ln.toTeacher : ln.ToTeacher,
        summary: ln.summary != null ? ln.summary : ln.Summary
      })
    },
    formatAdjustmentSummary({ handleType, handleKind, fromTeacher, toTeacher, summary }) {
      const ht = Number(handleType)
      const k = (handleKind || '').trim()
      const f = (fromTeacher || '').trim()
      const t = (toTeacher || '').trim()
      if (ht === 2 || k === '代课') {
        if (f && t) return `${t} 代替 ${f}`
      } else if (ht === 1 || k === '对调') {
        if (f && t) return `${f} 与 ${t} 对调`
      } else if (ht === 3 || k === '教务') {
        if (f && t) return `教务：${t} 接替 ${f}`
      }
      let s = (summary || '').trim()
      if (s) return this.normalizeLegacySubstitutionText(s)
      if (f && t) return `${t} 代替 ${f}`
      return '—'
    },
    /** 将旧文案里的「原 -> 现」读成现代替原（与列表原/现列一致） */
    normalizeLegacySubstitutionText(s) {
      if (!s || typeof s !== 'string') return ''
      return s.replace(/([^\s；;、]+?)\s*(?:->|→|➡)\s*([^\s；;、]+)/g, (_, a, b) => `${b} 代替 ${a}`)
    },
    /** 悬停：工资说明 + 结构化行（去重后拼接） */
    adjustmentTooltipFull(ev) {
      const payRaw = (this.field(ev, 'adjustmentPayrollNote') || this.field(ev, 'AdjustmentPayrollNote') || '').trim()
      const pay = payRaw ? this.normalizeLegacySubstitutionText(payRaw) : ''
      const lines = this.adjustmentChangeLines(ev)
      const lineText = lines.length ? lines.map((ln) => this.lineSummary(ln)).join('；') : ''
      if (pay && lineText) {
        const firstLine = this.lineSummary(lines[0])
        const same =
          firstLine && pay.replace(/\s/g, '').indexOf(firstLine.replace(/\s/g, '')) >= 0
        if (same) return pay
        return `${pay}；${lineText}`
      }
      if (lineText) return lineText
      return pay
    },
    /** 格内主行：优先结构化「代替/对调/教务」，否则用工资说明首句；略去与标题重复的时段括注 */
    adjustmentPrimaryLine(ev) {
      const timeName = (this.field(ev, 'timeName') || '').trim()
      const lines = this.adjustmentChangeLines(ev)
      if (lines.length) {
        let s = this.lineSummary(lines[0])
        if (timeName && s.endsWith(`（${timeName}）`)) s = s.slice(0, s.length - timeName.length - 2).trim()
        if (lines.length > 1) {
          return s.length > 14 ? `${s.slice(0, 12)}…等${lines.length}条` : `${s} 等`
        }
        return s.length > 30 ? s.slice(0, 28) + '…' : s
      }
      const raw = (this.field(ev, 'adjustmentPayrollNote') || this.field(ev, 'AdjustmentPayrollNote') || '').trim()
      if (!raw) return '见详情'
      const n = this.normalizeLegacySubstitutionText(raw)
      const first = (n.split('；')[0] || n).trim()
      return first.length > 34 ? first.slice(0, 32) + '…' : first
    },
    logRowSummary(row) {
      if (!row) return '—'
      return this.formatAdjustmentSummary({
        handleType: row.HandleType != null ? row.HandleType : row.handleType,
        handleKind: row.HandleKind != null ? row.HandleKind : row.handleKind,
        fromTeacher: row.FromTeacher != null ? row.FromTeacher : row.fromTeacher,
        toTeacher: row.ToTeacher != null ? row.ToTeacher : row.toTeacher,
        summary: row.Summary != null ? row.Summary : row.summary
      })
    },
    /** 申请原因说明（主表 ReasonDetail）；与「公/私」列区分展示 */
    changeLogReasonText(row) {
      if (!row) return '—'
      const d = (row.ReasonDetail != null ? row.ReasonDetail : row.reasonDetail || '').trim()
      return d || '—'
    },
    changeLogHasReasonDetail(row) {
      if (!row) return false
      return !!(row.ReasonDetail != null ? row.ReasonDetail : row.reasonDetail || '').trim()
    },
    /** 原因列悬停：类型 + 说明全文 */
    changeLogReasonFull(row) {
      if (!row) return ''
      const t = (row.ReasonTypeName != null ? row.ReasonTypeName : row.reasonTypeName || '').trim()
      const d = (row.ReasonDetail != null ? row.ReasonDetail : row.reasonDetail || '').trim()
      if (d) return (t && t !== '—' ? `${t}：` : '') + d
      return t || '—'
    },
    /** 时段 · 班次名，单行展示 */
    eventTitleLine(ev) {
      const t = (this.field(ev, 'timeName') || '').trim()
      const n = (this.field(ev, 'sessionName') || '').trim()
      if (t && n) return `${t} · ${n}`
      return n || t || '—'
    },
    /** 主教姓名，顿号连接（不用标签，省高） */
    teachersJoined(ev) {
      return this.teacherNames(ev).join('、') || ''
    },
    fmtDate(d) {
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${y}-${m}-${day}`
    },
    isClassDept(d) {
      return String((d && d.DeptOrgType) || '').toLowerCase() === 'class'
    },
    treeNodeClick(data) {
      this.departmentId = data.value
      this.campusId = null
      this.classDepartmentId = null
      this.teacherMasterId = null
      this.classOptions = []
      this.calendarDays = []
      this.changeLogRows = []
      this.changeLogDialogVisible = false
      this.loadCampusOptions().then(() => this.applyViewDefaultsAndLoad())
    },
    loadCampusOptions() {
      const departmentId = normalizeDeptId(this.departmentId)
      if (!departmentId) {
        this.campusOptions = []
        return Promise.resolve()
      }
      return getCampusList({ page: 1, intPageSize: 999, departmentId }).then((res) => {
        if (!deptIdsEqual(this.departmentId, departmentId)) return
        this.campusOptions = res.data && res.data.success
          ? ((res.data.response && res.data.response.data) || [])
          : []
      }).catch(() => {
        if (deptIdsEqual(this.departmentId, departmentId)) this.campusOptions = []
      })
    },
    /**
     * 左树选校后/切换「按班级·按教师」时：默认第一个校区、第一个班或第一位主教，并拉日历（不弹校验提示）
     */
    applyViewDefaultsAndLoad() {
      if (!this.departmentId) return Promise.resolve()
      if (!this.campusOptions || this.campusOptions.length === 0) {
        this.calendarDays = []
        return Promise.resolve()
      }
      if (this.campusId == null || this.campusId === '') {
        this.campusId = this.campusOptions[0].Id
      }
      if (this.viewMode === 'class') {
        this.teacherMasterId = null
        return this.loadClassOptions().then(() => {
          if (this.classOptions && this.classOptions.length) {
            this.classDepartmentId = this.classOptions[0].Id
          } else {
            this.classDepartmentId = null
            this.calendarDays = []
          }
          this.$nextTick(() => {
            if (this.validateCalendarContext(true)) this.loadCalendar({ silent: true })
          })
        })
      }
      this.classDepartmentId = null
      return this.loadTeacherOptions().then(() => {
        if (this.teacherOptions && this.teacherOptions.length) {
          this.teacherMasterId = this.teacherOptions[0].value
        } else {
          this.teacherMasterId = null
          this.calendarDays = []
        }
        this.$nextTick(() => {
          if (this.validateCalendarContext(true)) this.loadCalendar({ silent: true })
        })
      })
    },
    /** 校区变更：按班级时重载行政班；按教师时清空并重载主教列表 */
    onCampusFilterChange() {
      const pid = normalizeDeptId(this.campusId)
      if (!this.departmentId || !pid) {
        this.classOptions = []
        this.classDepartmentId = null
        this.teacherOptions = []
        this.teacherMasterId = null
        this.calendarDays = []
        return
      }
      if (this.viewMode === 'class') {
        this.classDepartmentId = null
        this.loadClassOptions().then(() => {
          if (this.classOptions.length) this.classDepartmentId = this.classOptions[0].Id
          this.$nextTick(() => {
            if (this.validateCalendarContext(true)) this.loadCalendar({ silent: true })
            else this.calendarDays = []
          })
        })
      } else {
        this.teacherMasterId = null
        this.loadTeacherOptions().then(() => {
          if (this.teacherOptions.length) this.teacherMasterId = this.teacherOptions[0].value
          this.$nextTick(() => {
            if (this.validateCalendarContext(true)) this.loadCalendar({ silent: true })
            else this.calendarDays = []
          })
        })
      }
    },
    loadClassOptions() {
      const pid = normalizeDeptId(this.campusId)
      if (!this.departmentId || !pid) {
        this.classOptions = []
        return Promise.resolve()
      }
      return getSubDepartmentList({ pid }).then((res) => {
        const result = res.data || res
        const raw = (result.response || []).slice()
        let list = raw.filter((d) => this.isClassDept(d))
        list.sort((a, b) => (a.Name || '').localeCompare(b.Name || '', 'zh-CN'))
        this.classOptions = list
      })
    },
    loadTeacherOptions() {
      if (!this.departmentId) {
        this.teacherOptions = []
        this.teacherMasterId = null
        return Promise.resolve()
      }
      this.teacherLoading = true
      const campusId = normalizeDeptId(this.campusId)
      const req = {
        page: 1,
        intPageSize: 500,
        departmentId: String(this.departmentId),
        teacherName: ''
      }
      if (campusId) req.campusId = campusId
      return getTeacherMasterListPage(req)
        .then((res) => {
          const result = res.data || res
          const rows =
            result.success && result.response && result.response.data
              ? result.response.data
              : []
          this.teacherOptions = rows.map((r) => ({
            value: r.Id,
            label: r.TeacherName || r.teacherName || `ID:${r.Id}`
          }))
        })
        .finally(() => {
          this.teacherLoading = false
        })
    },
    onTabChange() {
      this.calendarDays = []
      this.changeLogRows = []
      this.changeLogDialogVisible = false
      this.$nextTick(() => {
        if (!this.departmentId) return
        this.applyViewDefaultsAndLoad()
      })
    },
    /** 与查询、变动记录共用的机构/班级/教师校验；传 true 时不弹提示（用于默认筛选后拉数） */
    validateCalendarContext(silent) {
      if (!this.departmentId) {
        if (!silent) this.$message.error('请先在左侧选择学校或机构')
        return false
      }
      if (this.viewMode === 'class') {
        const cid = normalizeDeptId(this.classDepartmentId)
        if (!cid) {
          if (!silent) this.$message.warning('请选择校区与行政班')
          return false
        }
      } else {
        const cp = normalizeDeptId(this.campusId)
        if (!cp) {
          if (!silent) this.$message.warning('请先选择校区')
          return false
        }
        const tid = Number(this.teacherMasterId)
        if (!Number.isFinite(tid) || tid <= 0) {
          if (!silent) this.$message.warning('请选择教师')
          return false
        }
      }
      return true
    },
    getCalendarListParams() {
      const d = this.calendarMonth
      const start = new Date(d.getFullYear(), d.getMonth(), 1)
      const end = new Date(d.getFullYear(), d.getMonth() + 1, 0)
      const params = {
        departmentId: this.departmentId,
        startDate: this.fmtDate(start),
        endDate: this.fmtDate(end),
        viewMode: this.viewMode
      }
      if (this.viewMode === 'class') params.classDepartmentId = this.classDepartmentId
      else {
        params.teacherMasterId = this.teacherMasterId
        params.campusId = normalizeDeptId(this.campusId)
      }
      return params
    },
    openChangeLogDialog() {
      if (!this.validateCalendarContext()) return
      this.changeLogRows = []
      this.changeLogPage = 1
      this.changeLogDialogLoading = true
      this.changeLogDialogVisible = true
      this.$nextTick(() => {
        this.fetchChangeLogForDialog()
      })
    },
    fetchChangeLogForDialog() {
      const params = this.getCalendarListParams()
      getSpecialCourseScheduleChangeLog(params)
        .then((res) => {
          const r = res.data || res
          this.changeLogRows = r.success ? r.response || [] : []
          this.changeLogPage = 1
        })
        .catch(() => {
          this.changeLogRows = []
          this.changeLogPage = 1
        })
        .finally(() => {
          this.changeLogDialogLoading = false
        })
    },
    /** opts.silent 为 true 时不弹校验提示（换月、默认筛选后拉数） */
    loadCalendar(opts) {
      const silent = opts && opts.silent === true
      if (!this.validateCalendarContext(silent)) return
      const params = this.getCalendarListParams()
      this.loading = true
      getSpecialCourseScheduleCalendar(params)
        .then((res) => {
          const result = res.data || res
          this.calendarDays = result.success ? result.response || [] : []
        })
        .catch(() => {
          this.calendarDays = []
        })
        .finally(() => {
          this.loading = false
        })
    }
  },
  watch: {
    calendarYearMonthKey() {
      if (!this.departmentId) return
      if (this.viewMode === 'class' && !this.classDepartmentId) return
      if (this.viewMode === 'teacher' && (!this.campusId || !this.teacherMasterId)) return
      this.loadCalendar({ silent: true })
    },
    changeLogRows(list) {
      const n = (list || []).length
      const maxPage = Math.max(1, Math.ceil(n / this.changeLogPageSize))
      if (this.changeLogPage > maxPage) this.changeLogPage = maxPage
    }
  }
}
</script>

<style scoped>
.sc-cal-layout {
  min-height: 0;
}

.sc-cal-page {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.sc-cal-shell {
  --sc-cal-ink: #1f2a37;
  --sc-cal-muted: #6b7280;
  --sc-cal-line: #e5e9f0;
  --sc-cal-soft: #f4f7fb;
  --sc-cal-accent: #2563eb;
  --sc-cal-accent-soft: #eff6ff;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: #fff;
  border: 1px solid var(--sc-cal-line);
  border-radius: 12px;
  overflow: hidden;
}

/* —— 工具栏 —— */
.sc-cal-toolbar {
  flex: 0 0 auto;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px 16px;
  padding: 14px 16px;
  background: linear-gradient(180deg, #fafbfd 0%, #fff 100%);
  border-bottom: 1px solid var(--sc-cal-line);
}

.sc-cal-seg {
  display: inline-flex;
  padding: 3px;
  border-radius: 10px;
  background: #eef2f7;
  border: 1px solid #e2e8f0;
  flex-shrink: 0;
}

.sc-cal-seg__btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  padding: 7px 14px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: var(--sc-cal-muted);
  font-size: 13px;
  font-weight: 500;
  line-height: 1;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.sc-cal-seg__btn i {
  font-size: 14px;
}

.sc-cal-seg__btn:hover {
  color: var(--sc-cal-ink);
}

.sc-cal-seg__btn.is-active {
  background: #fff;
  color: var(--sc-cal-accent);
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08);
}

.sc-cal-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px 12px;
  flex: 1;
  min-width: 0;
}

.sc-cal-field {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.sc-cal-field__label {
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 600;
  color: var(--sc-cal-muted);
  letter-spacing: 0.02em;
}

.sc-cal-field__ctrl {
  width: 200px;
}

.sc-cal-field__ctrl /deep/ .el-input__inner {
  border-radius: 8px;
  border-color: #d8dee8;
}

.sc-cal-query {
  border-radius: 8px !important;
  padding-left: 14px !important;
  padding-right: 14px !important;
}

.sc-cal-changelog-btn {
  margin-left: auto;
  border-radius: 8px !important;
  color: var(--sc-cal-ink) !important;
  border-color: #d8dee8 !important;
}

/* —— 月份条 —— */
.sc-cal-monthbar {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px 8px;
}

.sc-cal-monthbar__title {
  display: flex;
  align-items: baseline;
  gap: 10px;
  min-width: 0;
}

.sc-cal-monthbar__ym {
  font-size: 20px;
  font-weight: 700;
  color: var(--sc-cal-ink);
  letter-spacing: -0.02em;
  line-height: 1.2;
}

.sc-cal-monthbar__hint {
  font-size: 12px;
  color: var(--sc-cal-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sc-cal-monthbar__nav /deep/ .el-button {
  border-radius: 8px;
  border-color: #d8dee8;
  color: #374151;
}

.sc-cal-monthbar__nav /deep/ .el-button + .el-button {
  margin-left: 6px;
}

/* —— 日历主体 —— */
.sc-cal-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 0 12px 14px;
}

.sc-cal-widget {
  background: transparent;
}

.sc-cal-widget /deep/ .el-calendar__header {
  display: none;
}

.sc-cal-widget /deep/ .el-calendar__body {
  padding: 0;
}

.sc-cal-widget /deep/ .el-calendar-table thead th {
  padding: 10px 0 8px;
  font-size: 12px;
  font-weight: 600;
  color: var(--sc-cal-muted);
  border-bottom: 1px solid var(--sc-cal-line);
}

.sc-cal-widget /deep/ .el-calendar-table tr {
  height: auto !important;
}

.sc-cal-widget /deep/ .el-calendar-table td {
  vertical-align: top;
  height: auto;
  border-color: var(--sc-cal-line);
  transition: background 0.15s ease;
}

.sc-cal-widget /deep/ .el-calendar-table td.is-selected,
.sc-cal-widget /deep/ .el-calendar-table td.is-today {
  background: transparent;
}

.sc-cal-widget /deep/ .el-calendar-table .el-calendar-day {
  min-height: 108px;
  height: auto !important;
  padding: 0;
}

.sc-cal-widget /deep/ .el-calendar-table .el-calendar-day:hover {
  background: transparent;
}

.sc-cal-cell {
  min-width: 0;
  min-height: 108px;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 8px 7px 8px;
  box-sizing: border-box;
}

.sc-cal-cell.is-other {
  background: #fafbfc;
}

.sc-cal-cell.is-other .sc-cal-cell__day {
  color: #c0c4cc;
}

.sc-cal-cell.is-other .sc-cal-ev {
  opacity: 0.55;
}

.sc-cal-cell.is-today {
  background: linear-gradient(180deg, var(--sc-cal-accent-soft) 0%, #fff 48%);
}

.sc-cal-cell.has-events:not(.is-other) {
  background: #fcfdff;
}

.sc-cal-cell__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  margin-bottom: 6px;
  flex-shrink: 0;
}

.sc-cal-cell__day {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  padding: 0 6px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  color: var(--sc-cal-ink);
  line-height: 1;
}

.sc-cal-cell.is-today .sc-cal-cell__day {
  background: var(--sc-cal-accent);
  color: #fff;
}

.sc-cal-cell__count {
  font-size: 11px;
  font-weight: 600;
  color: var(--sc-cal-accent);
  background: var(--sc-cal-accent-soft);
  border-radius: 999px;
  padding: 1px 7px;
  line-height: 16px;
}

.sc-cal-cell__events {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

/* —— 班次卡片（按时段着色） —— */
.sc-cal-ev {
  position: relative;
  min-width: 0;
  padding: 6px 7px 7px 9px;
  border-radius: 8px;
  border: 1px solid transparent;
  border-left-width: 3px;
  box-sizing: border-box;
  background: #fff;
}

.sc-cal-ev.tone-morning {
  background: #fff7ed;
  border-color: #fed7aa;
  border-left-color: #f97316;
}

.sc-cal-ev.tone-noon {
  background: #f0fdf4;
  border-color: #bbf7d0;
  border-left-color: #22c55e;
}

.sc-cal-ev.tone-eve1 {
  background: #eff6ff;
  border-color: #bfdbfe;
  border-left-color: #3b82f6;
}

.sc-cal-ev.tone-eve2 {
  background: #f5f3ff;
  border-color: #ddd6fe;
  border-left-color: #8b5cf6;
}

.sc-cal-ev.tone-adj {
  background: #fffbeb;
  border-color: #fde68a;
  border-left-color: #d97706;
}

.sc-cal-ev.tone-default {
  background: #f8fafc;
  border-color: #e2e8f0;
  border-left-color: #94a3b8;
}

.sc-cal-ev__time {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: inherit;
  opacity: 0.85;
  margin-bottom: 2px;
  line-height: 1.2;
}

.tone-morning .sc-cal-ev__time { color: #c2410c; }
.tone-noon .sc-cal-ev__time { color: #15803d; }
.tone-eve1 .sc-cal-ev__time { color: #1d4ed8; }
.tone-eve2 .sc-cal-ev__time { color: #6d28d9; }
.tone-adj .sc-cal-ev__time { color: #b45309; }
.tone-default .sc-cal-ev__time { color: #475569; }

.sc-cal-ev__head {
  color: var(--sc-cal-ink);
  font-weight: 600;
  font-size: 12px;
  line-height: 1.35;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  word-break: break-all;
}

.sc-cal-ev__meta {
  margin-top: 3px;
  color: #4b5563;
  font-size: 11px;
  line-height: 1.3;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sc-cal-ev__meta i {
  margin-right: 3px;
  font-size: 11px;
  color: #9ca3af;
}

.sc-cal-ev__adjblock {
  margin-top: 5px;
  min-width: 0;
}

.sc-cal-ev__adjline {
  display: flex;
  align-items: flex-start;
  gap: 5px;
  min-width: 0;
  padding: 3px 5px;
  border-radius: 5px;
  background: rgba(255, 255, 255, 0.72);
  border: 1px solid rgba(217, 119, 6, 0.28);
  box-sizing: border-box;
  cursor: help;
}

.sc-cal-ev__adj-pill {
  font-size: 10px;
  color: #92400e;
  background: #fef3c7;
  border: 1px solid #fcd34d;
  border-radius: 3px;
  padding: 0 4px;
  line-height: 15px;
  height: 15px;
  box-sizing: border-box;
  flex-shrink: 0;
}

.sc-cal-ev__adj-phrase {
  flex: 1;
  min-width: 0;
  font-size: 11px;
  line-height: 1.4;
  color: #78350f;
  font-weight: 500;
  word-break: break-all;
}

/* —— 变动记录弹窗 —— */
.sc-cal-chg-stack {
  line-height: 1.35;
  text-align: left;
}
.sc-cal-chg-sub {
  margin-top: 2px;
  font-size: 12px;
  color: #909399;
  line-height: 1.3;
}
.sc-cal-chg-ellipsis {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
}
.sc-cal-chg-ellipsis-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-all;
}
.sc-cal-chg-reason-type {
  color: #606266;
  font-weight: 500;
}
.sc-cal-chg-reason .sc-cal-chg-sub {
  margin-top: 4px;
}

.sc-cal-changelog__body /deep/ .toolbar {
  margin-top: 10px;
  margin-bottom: 0;
  overflow: hidden;
  clear: both;
}

.sc-cal-changelog__body {
  min-height: 0;
}

.sc-cal-changelog__empty {
  margin: 0;
  min-height: 220px;
  width: 100%;
  box-sizing: border-box;
  padding: 24px 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-size: 14px;
  color: #909399;
  line-height: 1.6;
}
</style>
